import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { timeAgo } from "@/lib/format";
import {
  ArrowDownLeft, ArrowUpRight, Activity, FileSpreadsheet, FileDown, CalendarRange, Mail,
} from "lucide-react";

const money = (v, c) =>
  `${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;

/** Every cash movement per currency, with the balance the client held at each point. */
export const MoneyTimeline = () => {
  const [summary, setSummary] = useState([]);
  const [ccy, setCcy] = useState(null);
  const [data, setData] = useState(null);
  const [mailing, setMailing] = useState(false);

  const emailSummary = async () => {
    setMailing(true);
    try {
      const { data: res } = await api.post("/money/monthly/email");
      toast.success(res.subject, {
        description: res.delivered
          ? "Sent to your inbox."
          : "DEMO MODE — recorded in your email log until a SendGrid key is added." });
    } catch (e) {
      toast.error("Couldn't send the summary", { description: e?.response?.data?.detail });
    } finally { setMailing(false); }
  };

  useEffect(() => {
    api.get("/money/summary").then(({ data }) => {
      const active = data.currencies.filter((c) => c.balance > 0 || c.movements > 0);
      setSummary(active);
      setCcy((prev) => prev || active[0]?.currency || "USD");
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!ccy) return;
    api.get("/money/timeline", { params: { currency: ccy, limit: 40 } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [ccy]);
  useEffect(() => { load(); }, [load]);

  if (!summary.length) return null;

  return (
    <Card className="p-5 shadow-premium mt-4 sm:mt-5" data-testid="money-timeline-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Activity className="h-4 w-4" />Money timeline
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Every payment in and out, per currency, with the balance you held at each point.
          </p>
        </div>
        {data && (
          <div className="flex items-center gap-4 text-xs">
            <div className="text-end">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">In</div>
              <div className="numeric font-semibold text-emerald-600" data-testid="timeline-total-in">
                {money(data.total_in, data.currency)}
              </div>
            </div>
            <div className="text-end">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Out</div>
              <div className="numeric font-semibold text-red-500" data-testid="timeline-total-out">
                {money(data.total_out, data.currency)}
              </div>
            </div>
            <div className="text-end">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Balance</div>
              <div className="numeric font-semibold" data-testid="timeline-balance">
                {money(data.balance, data.currency)}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-3.5 -mx-1 px-1 overflow-x-auto no-scrollbar">
        <div className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-secondary/50 p-1"
          data-testid="timeline-currency-tabs">
          {summary.map((c) => (
            <button key={c.currency} onClick={() => setCcy(c.currency)}
              data-testid={`timeline-ccy-${c.currency}`}
              className={`shrink-0 px-3 h-8 rounded-full text-xs transition-colors ${
                ccy === c.currency ? "bg-card text-foreground font-semibold shadow-premium"
                                  : "text-muted-foreground hover:text-foreground font-medium"}`}>
              {c.currency}
              <span className="ms-1.5 text-[10px] text-muted-foreground">{c.movements}</span>
            </button>
          ))}
        </div>
      </div>

      {data && (
        data.items.length ? (
          <div className="mt-3 divide-y" data-testid="timeline-rows">
            {data.items.map((r) => {
              const inbound = r.direction === "in";
              return (
                <div key={`${r.id}-${r.timestamp}-${r.direction}`}
                  className="flex items-center gap-3 py-2.5" data-testid={`timeline-row-${r.type}`}>
                  <span className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${
                    inbound ? "bg-emerald-500/12 text-emerald-600" : "bg-red-500/10 text-red-500"}`}>
                    {inbound ? <ArrowDownLeft className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">{r.label}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {r.counterparty || r.note || "—"} · {timeAgo(r.timestamp)}
                    </div>
                  </div>
                  <div className="text-end shrink-0">
                    <div className={`numeric text-xs font-semibold ${inbound ? "text-emerald-600" : ""}`}>
                      {inbound ? "+" : "−"}{money(r.amount, data.currency)}
                    </div>
                    <div className="numeric text-[11px] text-muted-foreground">
                      {money(r.running_balance, data.currency)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mt-4 text-xs text-muted-foreground">
            Nothing has moved in {data.currency} yet.
          </p>
        )
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="text-[10px]">
          Balances walked back from your live wallet
        </Badge>
        {data && (
          <>
            <Button variant="ghost" size="sm" className="h-7 gap-1 text-[11px]"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/money/timeline.csv?currency=${data.currency}`, "_blank")}
              data-testid="timeline-export-csv">
              <FileSpreadsheet className="h-3 w-3" />CSV
            </Button>
            <Button variant="ghost" size="sm" className="h-7 gap-1 text-[11px]"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/money/timeline.pdf?currency=${data.currency}`, "_blank")}
              data-testid="timeline-export-pdf">
              <FileDown className="h-3 w-3" />PDF statement
            </Button>
            <Button variant="ghost" size="sm" className="h-7 gap-1 text-[11px]"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/money/monthly.pdf`, "_blank")}
              data-testid="timeline-monthly-pdf">
              <CalendarRange className="h-3 w-3" />Monthly summary
            </Button>
            <Button variant="ghost" size="sm" className="h-7 gap-1 text-[11px]"
              disabled={mailing} onClick={emailSummary} data-testid="timeline-email-summary">
              <Mail className="h-3 w-3" />{mailing ? "Sending..." : "Email it to me"}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
};
